import styled, { css } from 'styled-components';
import curry from 'lodash/curry';
import { injectTypography } from '../../utils/injectTypography';
import { injectSpacing } from '../../api/spacing/injectSpacing';
import { injectOSXFontSmoothing } from '../../utils/injectOSXFontSmoothing';
const getStateColor = curry((statesFn, colorFn, props) => {
    const color = colorFn(props);
    const states = statesFn(props);
    if (props.disabled)
        return states.disabled || color;
    if (props.invalid)
        return states.invalid || color;
    return color;
});
const getSliderTrackColor = getStateColor((props) => ({
    disabled: props.theme.colors.form.muted.tertiary,
}));
const getSliderFillColor = getStateColor((props) => ({
    disabled: props.theme.colors.form.muted.secondary,
    invalid: props.theme.colors.form.error,
}));
const getOutputColor = getStateColor((props) => ({
    disabled: props.theme.colors.form.secondary,
    invalid: props.theme.colors.form.error,
}));
const injectThumb = css `
  width: var(--thumb-diameter);
  height: var(--thumb-diameter);

  background-color: ${(props) => props.theme.colors.ui.primaryInverse};
  border-color: ${getSliderFillColor((props) => props.theme.colors.form.tertiary)};
  border-style: solid;
  border-width: 1px;
  border-radius: 50%;

  appearance: none;
  pointer-events: auto;

  &:hover {
    border-color: ${getSliderFillColor((props) => props.theme.colors.form.strong)};
  }

  ${(props) => props.disabled
    ? css `
          cursor: not-allowed;
        `
    : css `
          cursor: grab;
          &:active {
            cursor: grabbing;
          }
        `}
`;
// When numberOfThumbs is 2 (dual slider) the container should be a `fieldset`,
// some styles here are a result of this.
export const StyledContainer = styled.div `
  margin: 0;
  padding: 0;

  border: 0;

  ${injectSpacing('margin')}
`;
export const StyledSliderContainer = styled.div `
  --range: calc(var(--max) - var(--min));

  --thumb-diameter: 26px;
  --thumb-radius: calc(var(--thumb-diameter) / 2);

  --output-size: 1ch;
  --track-size: 2px;

  --ratio0: calc((var(--value0) - var(--min)) / var(--range));
  --position0: calc(
    var(--thumb-radius) + (var(--ratio0) * (100% - var(--thumb-diameter)))
  );

  ${(props) => props.numberOfThumbs === 2 &&
    css `
      --ratio1: calc((var(--value1) - var(--min)) / var(--range));
      --position1: calc(
        var(--thumb-radius) + (var(--ratio1) * (100% - var(--thumb-diameter)))
      );

      --midpoint: calc((var(--position0) + var(--position1)) / 2);
    `}

  display: grid;
  grid-template-rows:
    [ ${"output" /* output */} ] auto
    var(${(props) => props.theme.responsive.spacing.xs})
    [ ${"track" /* track */} ] auto
    var(${(props) => props.theme.responsive.spacing.xxs})
    [ ${"bounds" /* bounds */} ] auto;
  align-items: center;
`;
export const StyledSlider = styled.input.attrs({
    type: 'range',
}) `
  grid-row: ${"track" /* track */};
  grid-column: 1;
  width: 100%;
  height: var(--thumb-diameter);
  margin: 0;
  padding: 0;

  background: transparent;
  outline-offset: var(${(props) => props.theme.responsive.spacing.xxs});

  appearance: none;

  pointer-events: none;

  &::-webkit-slider-thumb {
    ${injectThumb}
  }

  &:focus::-webkit-slider-thumb {
    border-color: ${getOutputColor((props) => props.theme.colors.form.strong)};
  }

  &::-moz-range-thumb {
    ${injectThumb}
  }

  &:focus::-moz-range-thumb {
    border-color: ${getOutputColor((props) => props.theme.colors.form.strong)};
  }

  &::-webkit-slider-track {
    pointer-events: none;
  }

  &::-moz-range-track {
    pointer-events: none;
  }
`;
export const StyledTrack = styled.div `
  --gradient-direction: to right;

  [dir='rtl'] & {
    --gradient-direction: to left;
  }

  grid-row: ${"track" /* track */};
  grid-column: 1;

  height: var(--track-size);

  ${(props) => {
    const fillColor = getSliderFillColor((props) => props.theme.colors.form.strong);
    const backgroundColor = getSliderTrackColor((props) => props.theme.colors.form.muted.secondary);
    switch (props.numberOfThumbs) {
        case 1:
            return css `
          background: linear-gradient(
            var(--gradient-direction),
            ${fillColor} var(--position0),
            ${backgroundColor} var(--position0)
          );
        `;
        case 2:
            return css `
          background: linear-gradient(
            var(--gradient-direction),
            ${backgroundColor} var(--start),
            ${fillColor} var(--start),
            ${fillColor} var(--end),
            ${backgroundColor} var(--end)
          );
        `;
        default:
            return css `
          background-color: ${backgroundColor};
        `;
    }
}}
`;
export const StyledOutput = styled.output `
  display: inline-block;
  grid-row: ${"output" /* output */};
  justify-self: center;

  font-variant-numeric: tabular-nums;

  color: ${getOutputColor((props) => props.theme.colors.form.primary)};

  ${StyledSlider}:hover ~ &,
  ${StyledSlider}:focus ~ & {
    color: ${getOutputColor((props) => props.theme.colors.form.strong)};
  }

  ${(props) => injectTypography(props, 'copy1')}
  ${injectOSXFontSmoothing(true)}
  ${(props) => props.numberOfThumbs === 1 &&
    css `
      @supports (width: min(1px, 2px)) and (width: max(1px, 2px)) {
        --output-position: max(
          0px,
          min(
            calc(var(--position0) - (var(--output-size) / 2)),
            calc(100% - var(--output-size))
          )
        );

        justify-self: start;
        margin-inline-start: var(--output-position);
      }
    `}
`;
export const StyledMinMax = styled.div `
  display: flex;
  grid-row: ${"bounds" /* bounds */};
  justify-content: space-between;

  ${(props) => injectTypography(props, 'copy2')}
  ${injectOSXFontSmoothing(true)}

  color: ${(props) => props.theme.colors.form.secondary};
`;
//# sourceMappingURL=Slider.styles.js.map